export function calculateTotals({
  planPriceRaw = 0,
  addonsPriceRaw = 0,
  discount = null,
  credits = 0,
  planCreditsAlreadyApplied = 0,
  creditsAutoApply = false,
}) {
  // ----------------------
  // 1) Apply discount
  // ----------------------
  let planAfterDiscount = planPriceRaw;
  let addonsAfterDiscount = addonsPriceRaw;

  if (discount) {
    const discountValue = parseFloat(discount.value || 0);
    if (discount.value_type === 'percentage') {
      const factor = Math.max(0, 1 - discountValue / 100);
      planAfterDiscount *= factor;
      addonsAfterDiscount *= factor;
    } else {
      // Fixed monetary amount – take from plan first, remainder from add-ons
      const remainderAfterPlan = Math.max(discountValue - planAfterDiscount, 0);
      planAfterDiscount = Math.max(planAfterDiscount - discountValue, 0);
      addonsAfterDiscount = Math.max(addonsAfterDiscount - remainderAfterPlan, 0);
    }
  }

  // ----------------------
  // 2) Credits already applied to plan server-side
  // ----------------------
  let planEffective = planAfterDiscount;
  if (creditsAutoApply) {
    planEffective = Math.max(planEffective - (planCreditsAlreadyApplied || 0), 0);
  }

  // ----------------------
  // 3) Apply customer credits (remaining balance)
  //    • Credits reduce add-ons first, then plan.
  // ----------------------
  let remainingCredits = parseFloat(credits || 0);

  const creditsOnAddons = Math.min(remainingCredits, addonsAfterDiscount);
  let addonsAfterCredits = addonsAfterDiscount - creditsOnAddons;

  remainingCredits -= creditsOnAddons;
  const creditsOnPlan = Math.min(remainingCredits, planEffective);
  let planAfterCredits = planEffective - creditsOnPlan;

  const total = Math.max(0, planAfterCredits + addonsAfterCredits);

  return {
    planAfterDiscount,
    addonsAfterDiscount,
    planAfterCredits,
    addonsAfterCredits,
    creditsOnAddons,
    creditsOnPlan,
    total: Number(total.toFixed(2)),
  };
}

// Recharge credits are order-level, but the portal frames them as an add-on
// budget (credits cover add-ons first, then the box — see calculateTotals; the
// total is the same either way). This is what's still left for a *new* add-on
// once the add-ons already in the box have taken their share.
export function getAddonCreditBudget({ credits = 0, addonsPrice = 0 }) {
  return Math.max((parseFloat(credits) || 0) - (parseFloat(addonsPrice) || 0), 0);
}

// How the remaining budget covers one add-on's price.
//   { coverage: 'full' }                       → credits cover the whole price
//   { coverage: 'partial', remainder, covered } → member pays `remainder`
//   null                                        → no credits left / no price
export function getCreditCoveredPrice(price, budget) {
  const priceNum = parseFloat(price) || 0;
  const budgetNum = parseFloat(budget) || 0;
  if (priceNum <= 0 || budgetNum <= 0) return null;
  if (budgetNum >= priceNum) return { coverage: 'full', remainder: 0, covered: priceNum };
  return {
    coverage: 'partial',
    remainder: Number((priceNum - budgetNum).toFixed(2)),
    covered: Number(budgetNum.toFixed(2)),
  };
}

// Card pricing for the credits nudge: top-up only. We never advertise an
// add-on as free — that just hands the balance back as a discount. Items the
// balance only partly covers are the ones worth pointing at: the member pays
// the remainder out of pocket, which is where the add-on revenue comes from.
// Returns the partial result from getCreditCoveredPrice, or null.
export function getCreditTopUpPrice(price, budget) {
  const pricing = getCreditCoveredPrice(price, budget);
  return pricing?.coverage === 'partial' ? pricing : null;
}

// Unit price for a product that is about to become a paid add-on, as a
// "14.00" string, or null when the product carries no usable price.
//
// Product objects reach the box from three shapes: Shopify Storefront
// (`variants[0].price = { amount: '14.0' }`), Recharge onetimes / Shopify REST
// (`'14.00'` strings), and Recharge's bundle-data feed, which is Shopify's
// Ajax product shape with INTEGER CENTS (`variants[0].price = 1400`,
// `price = 1400`). The old-design plan grid is built from that last feed, so
// a plan item converted to an add-on used to carry 1400 as its price and the
// box total read $1,511 instead of $125 (WIL-79). An add-on is never $100+,
// while its cents value always is, so whole numbers >= 100 are cents.
const rawAddonPrice = (value) => {
  if (value == null) return null;
  if (typeof value === 'object') return rawAddonPrice(value.amount);
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Number.isInteger(n) && n >= 100 ? n / 100 : n;
};

export function addonUnitPrice(product) {
  const candidates = [product?.variants?.[0]?.price, product?.price];
  for (const candidate of candidates) {
    const n = rawAddonPrice(candidate);
    if (n !== null) return n.toFixed(2);
  }
  return null;
}
